import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import {
  Button,
  ButtonGroup,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  InputGroup,
  InputGroupAddon,
  Label,
  UncontrolledTooltip,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import 'cropperjs/dist/cropper.css';

import { APIperformFileAIAction } from '../../api/Files';

import { sendTrackingEvent } from '../../helpers';
import { userCan } from '../../helpers/Auth';
import { addFlashMessage } from '../../helpers/FlashMessages';

import ImageEditorAIButton from './AIButton';

const ImageEditorSidebar = ({
  rotation,
  handleRotate,
  handleFlip,
  lockRatio,
  handleLockRatio,
  cropDimensions,
  cropRatioOptions,
  cropCategory,
  handleCropCategory,
  cropCategoryPresets,
  cropPreset,
  handleCropperDimensionsChange,
  handleCropPreset,
  handleCropperSwitch,
  mode,
  getFileToPerformAIAction,
  handleChangeSrc,
  callbackAIAction,
  cropperData,
  handlePerformingAIAction,
  fixedResolution,
  handleFixedResolution,
  AICollapseOpen,
  handleAICollapseOpen,
  handleResetImage,
}) => {
  const intl = useIntl();

  const [localRotation, setLocalRotation] = useState(0);
  const [cropDropdownIsOpen, setCropDropdownIsOpen] = useState(false);
  const [localCropDimensions, setLocalCropDimensions] = useState({ x: 0, y: 0 });
  const [backgroundRemoved, setBackgroundRemoved] = useState(false);
  const [foregroundErased, setForegroundErased] = useState(false);
  const [resolutionIncreased, setResolutionIncreased] = useState(false);

  const aiActions = [
    {
      name: 'remove-background',
      containerId: 'RemoveBackground',
      icon: 'fal fa-image-circle-xmark',
      button: intl.formatMessage({ defaultMessage: 'Remove background' }),
      done: backgroundRemoved,
      setDone: setBackgroundRemoved,
      success: intl.formatMessage({
        defaultMessage: 'Background removed',
      }),
      error: intl.formatMessage({
        defaultMessage: 'Something went wrong during background removal process',
      }),
      alreadyDoneMessage: intl.formatMessage({
        defaultMessage: 'Background already removed',
      }),
    },
    // {
    //   name: 'enhance-image',
    //   containerId: 'EnhanceImage',
    //   icon: 'fal fa-hand-sparkles',
    //   button: intl.formatMessage({ defaultMessage: 'Enhance image' }),
    //   done: imageEnhanced,
    //   setDone: setImageEnhanced,
    //   success: intl.formatMessage({
    //     defaultMessage: 'Image enhanced',
    //   }),
    //   error: intl.formatMessage({
    //     defaultMessage: 'Something went wrong during image enhancing process',
    //   }),
    //   alreadyDoneMessage: intl.formatMessage({
    //     defaultMessage: 'Image already enhanced',
    //   }),
    // },
    {
      name: 'erase-foreground',
      containerId: 'EraseForeground',
      icon: 'fal fa-eraser',
      button: intl.formatMessage({ defaultMessage: 'Erase foreground' }),
      done: foregroundErased,
      setDone: setForegroundErased,
      success: intl.formatMessage({
        defaultMessage: 'Foreground erased',
      }),
      error: intl.formatMessage({
        defaultMessage: 'Something went wrong during foreground erasing process',
      }),
      alreadyDoneMessage: intl.formatMessage({
        defaultMessage: 'Foreground already erased',
      }),
    },
    {
      name: 'blur-background',
      containerId: 'BlurBackground',
      icon: 'fal fa-droplet',
      button: intl.formatMessage({ defaultMessage: 'Blur background' }),
      done: false,
      setDone: () => {},
      success: intl.formatMessage({
        defaultMessage: 'Background blurred',
      }),
      error: intl.formatMessage({
        defaultMessage: 'Something went wrong during background blurring process',
      }),
      alreadyDoneMessage: intl.formatMessage({
        defaultMessage: 'Backgroud already blurred',
      }),
      actionMessage: intl.formatMessage({ defaultMessage: 'Perform blur' }),
    },
    {
      name: 'replace-background',
      containerId: 'ReplaceBackground',
      icon: 'fal fa-image-landscape',
      button: intl.formatMessage({ defaultMessage: 'Replace background' }),
      done: false,
      setDone: () => {},
      success: intl.formatMessage({
        defaultMessage: 'Background replaced',
      }),
      error: intl.formatMessage({
        defaultMessage: 'Something went wrong during background replacing process',
      }),
      alreadyDoneMessage: intl.formatMessage({
        defaultMessage: 'Backgroud already replaced',
      }),
      actionMessage: intl.formatMessage({ defaultMessage: 'Replace background' }),
    },
    {
      name: 'expand',
      containerId: 'Expand',
      icon: 'fal fa-camera-viewfinder',
      button: intl.formatMessage({ defaultMessage: 'Expand' }),
      done: false,
      setDone: () => {},
      success: intl.formatMessage({
        defaultMessage: 'Image expanded',
      }),
      error: intl.formatMessage({
        defaultMessage: 'Something went wrong during expanding the image',
      }),
      alreadyDoneMessage: intl.formatMessage({
        defaultMessage: 'Image already expanded',
      }),
      actionMessage: intl.formatMessage({ defaultMessage: 'Expand background' }),
    },
    {
      name: 'increase-resolution',
      containerId: 'IncreaseResolution',
      icon: 'fal fa-border-outer',
      button: intl.formatMessage({ defaultMessage: 'Increase resolution' }),
      done: resolutionIncreased,
      setDone: setResolutionIncreased,
      success: intl.formatMessage({
        defaultMessage: 'Resolution increased',
      }),
      error: intl.formatMessage({
        defaultMessage: 'Something went wrong when increasing resolution',
      }),
      alreadyDoneMessage: intl.formatMessage({
        defaultMessage: 'Image resolution already increased',
      }),
      actionMessage: intl.formatMessage({ defaultMessage: 'Perform increase' }),
    },
  ];

  const actionsProvider = (actionName) => aiActions.find(a => a.name === actionName);

  useEffect(() => {
    setLocalRotation(rotation);
  }, [rotation]);

  useEffect(() => {
    setLocalCropDimensions({ x: cropDimensions.x, y: cropDimensions.y });
  }, [cropDimensions]);

  const handleCropPresetWrapper = (preset) => {
    handleCropPreset(preset);
  };

  const handleLocalRotation = (e) => {
    const { value } = e.target;

    setLocalRotation(value);
    if (!Number.isNaN(Number(value))) {
      handleRotate(value, 'absolute');
    }
  };

  const handleCropperDimensionsWrapper = (event) => {
    const { value, id } = event.target;
    const parsedValue = Number(parseInt(value));

    const isProperValue = !!(!Number.isNaN(parsedValue) && parsedValue > 0);

    if (isProperValue) {
      if (id === 'cropWidth') handleCropperDimensionsChange(parsedValue, 'width');
      if (id === 'cropHeight') handleCropperDimensionsChange(parsedValue, 'height');
    } else {
      if (id === 'cropWidth') {
        setLocalCropDimensions(prevValue => ({
          ...prevValue,
          x: value,
        }));
      }
      if (id === 'cropHeight') {
        setLocalCropDimensions(prevValue => ({
          ...prevValue,
          y: value,
        }));
      }
    }
  };

  const performAction = async (actionName, params = {}) => {
    const action = actionsProvider(actionName);

    if (!action) return;

    handlePerformingAIAction(true);
    const file = await getFileToPerformAIAction();

    if (!file) return;

    sendTrackingEvent(`file-editor-ai-${actionName}`);

    const formData = new FormData();
    formData.append('image', file);
    formData.append('action', actionName);
    if (params.scale) formData.append('scale', params.scale);
    if (params.prompt) formData.append('prompt', params.prompt);
    if (params.canvas_size) {
      formData.append('canvas_size', JSON.stringify(params.canvas_size));
    }
    if (params.desired_amount) formData.append('desired_amount', params.desired_amount);

    if (params.reference_image) {
      const response = await fetch(params.reference_image.urls.original);
      const blob = await response.blob();

      const reference = new File(
        [blob],
        params.reference_image.original_name,
        { type: params.reference_image.type },
      );

      formData.append('reference_image', reference);
    }

    if (params.original_image_location) {
      formData.append(
        'original_image_location',
        JSON.stringify(params.original_image_location),
      );
    }

    if (params.original_image_size) {
      formData.append(
        'original_image_size',
        JSON.stringify(params.original_image_size),
      );
    }

    APIperformFileAIAction(formData).then(async (response) => {
      const blob = response.data;
      const url = URL.createObjectURL(blob);
      action.setDone(true);
      handleChangeSrc(url, action.success);
    }).catch(() => {
      addFlashMessage({
        type: 'danger',
        text: action.error,
      });
    }).finally(() => {
      handlePerformingAIAction(false);
    });
  };

  const handleResetImageWrapper = () => {
    setBackgroundRemoved(false);
    setForegroundErased(false);
    setResolutionIncreased(false);

    handleResetImage();
  };

  const disableCrop = mode === 'clear';

  return (
    <>
      <div className="image-editor-controls-row">
        <div className="image-editor-controls image-editor-controls-no-grow">
          <div>
            <ButtonGroup>
              <Button
                onClick={handleResetImageWrapper}
                size="sm"
                className="text-nowrap"
              >
                <i className="fal fa-rotate mr-2" />
                {intl.formatMessage({ defaultMessage: 'Reset image' })}
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </div>
      <div className="image-editor-controls-row">
        <div className="image-editor-controls">
          <p>{intl.formatMessage({ defaultMessage: 'Rotate' })}</p>
          <div>
            <ButtonGroup>
              <Button
                onClick={() => handleRotate(-90)}
                id="ImageEditorRotateLeft"
                size="sm"
              >
                <i className="far fa-rotate-left" />
              </Button>
              <UncontrolledTooltip fade={false} placement="top" target="ImageEditorRotateLeft">
                {intl.formatMessage({ defaultMessage: 'Rotate left' })}
              </UncontrolledTooltip>
              <Button
                onClick={() => handleRotate(90)}
                id="ImageEditorRotateRight"
                size="sm"
              >
                <i className="far fa-rotate-right" />
              </Button>
              <UncontrolledTooltip fade={false} placement="top" target="ImageEditorRotateRight">
                {intl.formatMessage({ defaultMessage: 'Rotate right' })}
              </UncontrolledTooltip>
              <Button
                onClick={() => handleRotate(0, 'absolute')}
                id="ImageEditorRotateReset"
                size="sm"
              >
                <i className="far fa-trash-restore" />
              </Button>
              <UncontrolledTooltip fade={false} placement="top" target="ImageEditorRotateReset">
                {intl.formatMessage({ defaultMessage: 'Reset rotation' })}
              </UncontrolledTooltip>
            </ButtonGroup>
            <InputGroup size="sm">
              <Input
                value={localRotation}
                onChange={handleLocalRotation}
                className="image-editor-no-margin"
                bsSize="sm"
              />
              <InputGroupAddon addonType="append" size="sm">
                {intl.formatMessage({ defaultMessage: 'deg' })}
              </InputGroupAddon>
            </InputGroup>
          </div>
        </div>
        <div className="image-editor-controls image-editor-controls-no-grow">
          <p>{intl.formatMessage({ defaultMessage: 'Flip' })}</p>
          <div>
            <ButtonGroup>
              <Button
                onClick={() => handleFlip('horizontal')}
                id="ImageEditorFlipHorizontal"
                size="sm"
              >
                <i className="far fa-reflect-horizontal" />
              </Button>
              <UncontrolledTooltip fade={false} placement="top" target="ImageEditorFlipHorizontal">
                {intl.formatMessage({ defaultMessage: 'Flip horizontal' })}
              </UncontrolledTooltip>
              <Button
                onClick={() => handleFlip('vertical')}
                id="ImageEditorFlipVertical"
                size="sm"
              >
                <i className="far fa-reflect-vertical" />
              </Button>
              <UncontrolledTooltip fade={false} placement="top" target="ImageEditorFlipVertical">
                {intl.formatMessage({ defaultMessage: 'Flip vertical' })}
              </UncontrolledTooltip>
            </ButtonGroup>
          </div>
        </div>
      </div>
      <div className="image-editor-controls-row">
        <div className="image-editor-controls">
          <p>{intl.formatMessage({ defaultMessage: 'Tools' })}</p>
          <div className="image-editor-controls-column">
            {aiActions.map(action => (
              <ImageEditorAIButton
                key={action.name}
                action={actionsProvider(action.name)}
                perform={(params = {}) => performAction(action.name, params)}
                callbackAIAction={callbackAIAction}
                collapseOpen={AICollapseOpen}
                handleCollapseOpen={handleAICollapseOpen}
                cropperData={cropperData}
                cropDimensions={cropDimensions}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="image-editor-controls-row">
        <div className="image-editor-controls">
          <p>{intl.formatMessage({ defaultMessage: 'Crop' })}</p>
          <div className="switch-list">
            <Label for="switch-crop" className="switch">
              <div className="switch-details">
                {intl.formatMessage({ defaultMessage: 'Enable image cropping' })}
              </div>
              <div className="switch-control input-inner">
                <Input
                  type="checkbox"
                  name="switchCrop"
                  id="switch-crop"
                  value="true"
                  checked={mode === 'crop'}
                  onChange={handleCropperSwitch}
                />
                <i />
              </div>
            </Label>
          </div>
          <div className="image-editor-controls-presets-dropdown-wrapper">
            <div className={classnames({ disabled: disableCrop })}>
              <Dropdown
                isOpen={cropDropdownIsOpen}
                toggle={() => setCropDropdownIsOpen(!cropDropdownIsOpen)}
                size="sm"
              >
                <DropdownToggle>
                  {cropCategory.label}
                  <i className="far fa-caret-down" />
                </DropdownToggle>

                <DropdownMenu>
                  {[...cropRatioOptions.default].map((option) => (
                    <DropdownItem
                      key={option.key}
                      onClick={() => handleCropCategory(option)}
                    >
                      {option.label}
                    </DropdownItem>
                  ))}
                  <DropdownItem divider />
                  <DropdownItem
                    onClick={() => handleCropCategory({
                      label: intl.formatMessage({ defaultMessage: 'Custom' }),
                      key: 'custom',
                    })}
                  >
                    {intl.formatMessage({ defaultMessage: 'Custom' })}
                  </DropdownItem>
                  <DropdownItem divider />
                  <DropdownItem
                    onClick={() => handleCropCategory({
                      label: intl.formatMessage({ defaultMessage: 'Saved presets' }),
                      key: 'saved-presets',
                    })}
                  >
                    {intl.formatMessage({ defaultMessage: 'Saved presets' })}
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
            <div>
              {(cropCategory.key === 'saved-presets' && userCan('manage_settings')) && (
                <Link to="/admin/settings/image-editor" target="_blank">
                  {intl.formatMessage({ defaultMessage: 'Create a preset size here' })}
                </Link>
              )}
            </div>
          </div>
          <div className="image-editor-controls-presets-container">
            {cropCategoryPresets.map(preset => {
              const max = Math.max(preset.x, preset.y);
              const xDimension = max === preset.x ? 100 : (preset.x / preset.y) * 100;
              const yDimension = max === preset.y ? 100 : (preset.y / preset.x) * 100;

              return (
                <div
                  key={preset.label}
                  className={classnames('image-editor-preset-button-wrapper', { 'image-editor-preset-button-wrapper-active': _.isEqual(preset, cropPreset) && mode === 'crop' })}
                >
                  <Button
                    onClick={() => handleCropPresetWrapper(preset)}
                    color="link"
                    className="image-editor-preset-button"
                  >
                    <div className="image-editor-preset-box">
                      <div style={{
                        width: `${xDimension}%`,
                        height: `${yDimension}%`,
                      }}
                      />
                    </div>
                    <div className="image-editor-preset-labels">
                      <p>{preset.label}</p>
                      <p>
                        {intl.formatMessage({
                          defaultMessage: '{x} / {y} px',
                        }, {
                          x: preset.x,
                          y: preset.y,
                        })}
                      </p>
                    </div>
                  </Button>
                </div>
              );
            })}
          </div>

          {(cropCategoryPresets.length === 0 && cropCategory.key !== 'custom') && (
            <div>
              <p>{intl.formatMessage({ defaultMessage: 'No presets found.' })}</p>
            </div>
          )}

          {cropCategory.key === 'custom' && (
            <div className={classnames('image-editor-crop-labels', { disabled: disableCrop })}>
              <Label for="cropWidth" className="label-column">
                <span>{intl.formatMessage({ defaultMessage: 'Width' })}</span>
                <InputGroup>
                  <Input
                    type="text"
                    id="cropWidth"
                    value={localCropDimensions.x}
                    onChange={handleCropperDimensionsWrapper}
                  />
                  <InputGroupAddon addonType="append">{intl.formatMessage({ defaultMessage: 'px' })}</InputGroupAddon>
                </InputGroup>
              </Label>
              <Label for="cropHeight" className="label-column">
                <span>{intl.formatMessage({ defaultMessage: 'Height' })}</span>
                <InputGroup>
                  <Input
                    type="text"
                    id="cropHeight"
                    value={localCropDimensions.y}
                    onChange={handleCropperDimensionsWrapper}
                  />
                  <InputGroupAddon addonType="append">{intl.formatMessage({ defaultMessage: 'px' })}</InputGroupAddon>
                </InputGroup>
              </Label>
              <div className={classnames('image-editor-lock-ratio', { disabled: disableCrop })}>
                <div className="mb-2">
                  <Button
                    size="sm"
                    color={lockRatio ? 'primary' : 'secondary'}
                    onClick={() => handleLockRatio(!lockRatio)}
                    className="mr-1"
                  >
                    {lockRatio ? (
                      <i className="far fa-link-simple fa-fw" />
                    ) : (
                      <i className="far fa-link-simple-slash fa-fw" />
                    )}
                  </Button>
                  <span>{intl.formatMessage({ defaultMessage: 'Lock ratio' })}</span>
                </div>
                <div>
                  <Button
                    size="sm"
                    color={fixedResolution ? 'primary' : 'secondary'}
                    onClick={() => handleFixedResolution(!fixedResolution)}
                    className="mr-1"
                  >
                    {fixedResolution ? (
                      <i className="far fa-lock fa-fw" />
                    ) : (
                      <i className="far fa-unlock fa-fw" />
                    )}
                  </Button>
                  <span>{intl.formatMessage({ defaultMessage: 'Lock resolution' })}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const mapStateToProps = state => ({
  clientSettings: state.App.settings,
});

ImageEditorSidebar.propTypes = {
  handleRotate: PropTypes.func.isRequired,
  handleFlip: PropTypes.func.isRequired,
  rotation: PropTypes.number.isRequired,
  lockRatio: PropTypes.bool.isRequired,
  handleLockRatio: PropTypes.func.isRequired,
  cropDimensions: PropTypes.object.isRequired,
  cropRatioOptions: PropTypes.object.isRequired,
  handleCropperDimensionsChange: PropTypes.func.isRequired,
  handleCropperSwitch: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  cropCategory: PropTypes.object.isRequired,
  handleCropCategory: PropTypes.func.isRequired,
  cropPreset: PropTypes.object,
  handleCropPreset: PropTypes.func.isRequired,
  cropCategoryPresets: PropTypes.array.isRequired,
  getFileToPerformAIAction: PropTypes.func.isRequired,
  handleChangeSrc: PropTypes.func.isRequired,
  callbackAIAction: PropTypes.func.isRequired,
  cropperData: PropTypes.object,
  handlePerformingAIAction: PropTypes.func.isRequired,
  fixedResolution: PropTypes.bool.isRequired,
  handleFixedResolution: PropTypes.func.isRequired,
  AICollapseOpen: PropTypes.string,
  handleAICollapseOpen: PropTypes.func.isRequired,
  handleResetImage: PropTypes.func.isRequired,
};

ImageEditorSidebar.defaultProps = {
  cropPreset: null,
  cropperData: null,
  AICollapseOpen: null,
};

export default connect(mapStateToProps)(ImageEditorSidebar);
